import { Injectable } from '@angular/core';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { DateHelper } from '../../helpers/date.helper';
import { DateRange } from '../../types/date-range.type';
import { EventLogFilter, LicenseLimitViolation, MonitoringStatistics, ProcessDetail, ProcessInfo, ProcessOverview, ResourceInfo, SchedulerEvent, TenantBaseModel } from '../../types/generated.types';
import { Session } from '../../types/session.type';
import { Item } from '../../types/item.type';
import { SessionsFilter } from '../../types/sessions-filter.type';
import { BaseApiService } from './base-api.service';
import { ItemsFilter } from '../../types/items-filter.type';
import { ProcessAndQueueList } from '../../types/monitoring/process-and-queue-list.type';

@Injectable({ providedIn: 'root' })
export class MonitoringApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/monitoring`;
  }

  async getDatesWithData(processGroupId?: string): Promise<DateRange[]> {
    const datesWithData = await this.get<DateRange[]>(`dates-with-data/${processGroupId ?? ''}`);
    ApiConversionHelper.convertDates(datesWithData, ['start', 'end']);
    return datesWithData.map(x => new DateRange(DateHelper.startOfDay(x.start), DateHelper.endOfDay(x.end)));
  }

  async getProcesses(tenantId: string, from: Date, to: Date): Promise<ProcessOverview[]> {
    const result = await this.get<ProcessOverview[]>(`processes/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`);
    result.forEach(p => ApiConversionHelper.convertMonitoringProcessOverview(p));
    return result;
  }

  async getProcessesDetail(tenantId: string | null, includePendings: boolean = false): Promise<ProcessDetail[]> {
    const result = await this.get<ProcessDetail[]>(`processes/detail${this.getTenantParameter(tenantId)}`, { includePendings });
    result.forEach(d => ApiConversionHelper.convertMonitoringProcessDetail(d));
    return result;
  }

  async getProcessList(tenantId?: string, from?: Date, to?: Date, includeDeleted?: boolean): Promise<ProcessInfo[]> {
    return await this.get<ProcessInfo[]>(`process-list${this.getTenantParameter(tenantId)}`, {
      ...this.getIntervalParameters(from, to),
      ...(includeDeleted != null ? { includeDeleted } : {}),
    });
  }

  async getProcessInfo(processId: string): Promise<ProcessInfo> {
    return await this.get<ProcessInfo>(`process-info/${processId}`);
  }

  private getIntervalParameters(from?: Date, to?: Date): { from?: string; to?: string } {
    return from != null && to != null ? { from: DateHelper.formatApiDateTime(from), to: DateHelper.formatApiDateTime(to) } : {};
  }

  async getQueueList(tenantId?: string, from?: Date, to?: Date): Promise<TenantBaseModel[]> {
    return await this.get<TenantBaseModel[]>(`queue-list${this.getTenantParameter(tenantId)}`, this.getIntervalParameters(from, to));
  }

  async getTagList(tenantId?: string): Promise<string[]> {
    return await this.get<string[]>(`process-tag-list${this.getTenantParameter(tenantId)}`);
  }

  async getProcessFolderList(tenantId: string): Promise<string[]> {
    return await this.get<string[]>(`process-folder-list${this.getTenantParameter(tenantId)}`);
  }

  async getResourceList(tenantId?: string, from?: Date, to?: Date, includeDeleted?: boolean): Promise<ResourceInfo[]> {
    return await this.get<ResourceInfo[]>(`resource-list${this.getTenantParameter(tenantId)}`, {
      ...this.getIntervalParameters(from, to),
      ...(includeDeleted != null ? { includeDeleted } : {}),
    });
  }

  async getResourceInfo(resourceId: string): Promise<ResourceInfo> {
    return await this.get<ResourceInfo>(`resource-info/${resourceId}`);
  }

  async getSessions(tenantId: string, from: Date, to: Date, pageSize: number, pageIndex: number, filter: SessionsFilter): Promise<Session[]> {
    const result = await this.get<Session[]>(`sessions/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { ...filter, pageSize, pageIndex });

    result.forEach(s => ApiConversionHelper.convertMonitoringProcessSession(s));
    return result;
  }

  async getPendingSessions(tenantId: string, count: number): Promise<Session[]> {
    const result = await this.get<Session[]>(`sessions/pending${this.getTenantParameter(tenantId)}`, { count });
    result.forEach(s => ApiConversionHelper.convertMonitoringProcessSession(s));
    return result;
  }

  async deleteSession(session: Session): Promise<void> {
    return await this.delete(`session/${session.id}`);
  }

  async getPendingItems(tenantId: string, count: number): Promise<Item[]> {
    const result = await this.get<Item[]>(`items/pending${this.getTenantParameter(tenantId)}`, { count });
    result.forEach(i => ApiConversionHelper.convertMonitoringProcessItem(i));
    return result;
  }

  async getItems(from: Date, to: Date, pageSize: number, pageIndex: number, filter: ItemsFilter): Promise<Item[]> {
    const result = await this.get<Item[]>('items', {
      ...filter,
      pageSize,
      pageIndex,
      from: DateHelper.formatApiDateTime(from),
      to: DateHelper.formatApiDateTime(to),
    });

    result.forEach(i => ApiConversionHelper.convertMonitoringProcessItem(i));
    return result;
  }

  async deleteItem(item: Item): Promise<void> {
    return await this.delete(`item/${item.id}`);
  }

  async getSchedulerEventLogDatesWitData(): Promise<DateRange[]> {
    const datesWithData = await this.get<DateRange[]>('scheduler-events/dates-with-data');
    ApiConversionHelper.convertDates(datesWithData, ['start', 'end']);
    return datesWithData.map(x => new DateRange(DateHelper.startOfDay(x.start), DateHelper.endOfDay(x.end)));
  }

  async getSchedulerEventLog(tenantId: string, from: Date, to: Date, pageSize: number, pageIndex: number, filter: EventLogFilter): Promise<SchedulerEvent[]> {
    const body = { filter, pagination: { pageIndex, pageSize } };
    // we have to use POST because URL might be too long because of filters
    const result = await this.postWithResult<any, SchedulerEvent[]>(`scheduler-events/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, body, null, true);
    ApiConversionHelper.convertDates(result, ['timeStamp', 'firstOccurrence']);
    result?.forEach(x => ApiConversionHelper.convertDates(x, ['endOfQuarantine']));
    return result;
  }

  async getStatistics(tenantId: string): Promise<MonitoringStatistics> {
    return await this.get<MonitoringStatistics>(`statistics${this.getTenantParameter(tenantId)}`);
  }

  async getLicenseLimitViolations(): Promise<LicenseLimitViolation[]> {
    return await this.get<LicenseLimitViolation[]>('license-limit-violations');
  }

  // HELPER METHODS
  async getProcessAndQueueList(tenantId?: string, from?: Date, to?: Date): Promise<ProcessAndQueueList> {
    const data = await Promise.all([this.getProcessList(tenantId, from, to), this.getQueueList(tenantId, from, to)]);
    return {
      processes: data[0],
      queues: data[1],
    };
  }
}
